import React, { useState, useEffect } from 'react';
import { Pool } from '../types';
import { apiService, formatters } from '../services/api';
import HorizontalCoinList from '../components/HorizontalCoinList';
import './HomePage.css';

interface HomePageProps {
  onPoolSelect: (poolId: string) => void;
}

const HomePage: React.FC<HomePageProps> = ({ onPoolSelect }) => {
  const [pools, setPools] = useState<Pool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPool, setSelectedPool] = useState<string>('');
  const [minerAddress, setMinerAddress] = useState('');
  const [globalStats, setGlobalStats] = useState({
    totalHashrate: 0,
    totalMiners: 0,
    totalBlocks: 0
  });

  useEffect(() => {
    loadPools();
  }, []);

  const loadPools = async () => {
    try {
      setLoading(true);
      setError(null);
      const poolData = await apiService.getPools();
      let totalBlocks = poolData.reduce((acc, pool) => acc + (pool.totalBlocks || 0), 0);
      
      if (!totalBlocks) {
        try {
          const recentBlocks = await apiService.getAllBlocks(100);
          if (Array.isArray(recentBlocks)) {
            totalBlocks = recentBlocks.length;
          }
        } catch (blockErr) {
          console.warn('Failed to load global block stats:', blockErr);
        }
      }
      
      console.log('Loaded pools:', poolData);
      
      // Ensure poolData is an array
      if (!Array.isArray(poolData)) {
        console.error('Pool data is not an array:', poolData);
        setError('Invalid pool data format received');
        return;
      }
      
      setPools(poolData);
      
      // Calculate global stats safely
      const stats = poolData.reduce((acc, pool) => ({
        totalHashrate: acc.totalHashrate + (pool.hashrate || 0),
        totalMiners: acc.totalMiners + (pool.minerCount || 0),
        totalBlocks: acc.totalBlocks + (pool.totalBlocks || 0)
      }), { totalHashrate: 0, totalMiners: 0, totalBlocks: 0 });
      
      stats.totalBlocks = stats.totalBlocks || totalBlocks;
     
      setGlobalStats(stats);
      
      if (poolData.length > 0) {
        setSelectedPool(poolData[0].id);
      }
    } catch (err) {
      setError('Failed to load pool data');
      console.error('Error loading pools:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMinerLookup = () => {
    if (!selectedPool || !minerAddress.trim()) {
      alert('Please select a pool and enter a miner address');
      return;
    }
    
    // Store the values in sessionStorage for the dashboard to pick up
    sessionStorage.setItem('dashboardPool', selectedPool);
    sessionStorage.setItem('dashboardAddress', minerAddress);
    
    // Navigate to dashboard
    console.log('Navigating to dashboard with pool:', selectedPool, 'address:', minerAddress);
    window.location.hash = '#dashboard';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading"></div>
        <p>Loading pools...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={loadPools} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <h2>Welcome to Cryptoware</h2>
        <p>High performance Kaspa mining pool</p>
        
        {/* Global Stats */}
        <div className="global-stats">
          <div className="stat-card">
            <div className="stat-value">{formatters.hashrate(globalStats.totalHashrate)}</div>
            <div className="stat-label">Total Hashrate</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{globalStats.totalMiners}</div>
            <div className="stat-label">Active Miners</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{globalStats.totalBlocks}</div>
            <div className="stat-label">Recent Blocks</div>
          </div>
        </div>
      </section>

      {/* Currency Section */}
      <section className="pools-section">
        <h3>Select a coin to get started</h3>
        <HorizontalCoinList pools={pools} />
        <br />
        <p>* All ports are vardiff and will change your difficulty to match your miner's hashrate. Use <strong style={{ color:'green' }}>D=xxx</strong> in the password section to set your prefered static diff.</p>
      </section>
    </div>
  );
};

export default HomePage;
